import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Typography,
  Container,
  Box,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  TextField,
  Button,
  Divider,
  Fab,
  Stack,
  Grid,
  Popover,
  ThemeProvider,
  createTheme,
  Paper,
  CssBaseline,
  Tooltip,
  Modal,
  CircularProgress
} from '@mui/material';
import DOMPurify from 'dompurify';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShareIcon from '@mui/icons-material/Share';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import SendIcon from '@mui/icons-material/Send';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { get, post, del } from '../utility/fetchWeb';
import { post as Posts } from '../utility/fetch';
import { post as postAdmin } from '../utility/fetchAdmin';


import { Close, Groups, Repeat } from '@mui/icons-material';
import defaultImage from '../Assets/images/profile-pix.jpg';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PostModal from './Layout/PostModal';
import notification from '../utility/notification';
import Hls from 'hls.js';
import PromoBanner from './Layout/PromoBanner';
import FlagIcon from '@mui/icons-material/Flag';
import ReportIcon from '@mui/icons-material/Report';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

const reactions = [
  { type: 1, emoji: '👍', key: 'likesCount' },
  { type: 2, emoji: '❤️', key: 'loveCount' },
  { type: 3, emoji: '😂', key: 'laughCount' },
  { type: 4, emoji: '😮', key: 'wowCount' },
  { type: 5, emoji: '😢', key: 'sadCount' },
];

const theme = createTheme({
  palette: {
    primary: {
      main: '#28a745',
    },
    secondary: {
      main: '#f8f9fa',
    },
    background: {
      default: '#f8f9fa',
      paper: '#fff',
    },
    text: {
      primary: '#333',
      secondary: '#6c757d',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
    body1: {
      fontSize: '0.95rem',
    },
    body2: {
      fontSize: '0.85rem',
      color: '#6c757d',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#6c757d',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '25px',
          paddingLeft: '18px',
          paddingRight: '18px',
        },
        containedPrimary: {
          backgroundColor: '#28a745',
          '&:hover': {
            backgroundColor: '#218838',
          }
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '25px',
            backgroundColor: '#eafcec',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: '#cce5d6',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#28a745',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#6c757d',
          '&.Mui-disabled': {
            color: '#bdbdbd',
          }
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: '#007bff',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#0056b3',
          }
        }
      }
    }
  },
});

const getTotalReactions = (post) => {
  return reactions.reduce((total, reaction) => total + (post[reaction.key] || 0), 0);
};

// Utility function to convert plain text URLs to clickable links
const linkifyText = (text) => {
  if (!text) return '';
  
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Split text by URLs and map to JSX
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#28a745',
            textDecoration: 'none',
            fontWeight: 500,
            wordBreak: 'break-all'
          }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// Function to add target="_blank" to all links in HTML content and linkify plain text URLs
const processPostContent = (htmlContent) => {
  // First, sanitize the HTML
  let sanitized = DOMPurify.sanitize(htmlContent || '', {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    ALLOWED_STYLES: {
      '*': {
        'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
        'color': [/^#[0-9a-fA-F]{3,6}$/],
        'background-color': [/^#[0-9a-fA-F]{3,6}$/],
      }
    }
  });
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitized;
  
  // Function to recursively process text nodes and convert URLs to links
  const processTextNodes = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      // Regex to match URLs (more comprehensive)
      const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
      
      if (urlRegex.test(text)) {
        // Create a temporary container for the new content
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        
        // Reset regex
        urlRegex.lastIndex = 0;
        let match;
        
        while ((match = urlRegex.exec(text)) !== null) {
          // Add text before the URL
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
          }
          
          // Create link element
          const link = document.createElement('a');
          link.href = match[0];
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.textContent = match[0];
          fragment.appendChild(link);
          
          lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }
        
        // Replace the text node with the fragment
        node.parentNode.replaceChild(fragment, node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'A') {
      // Don't process text inside existing anchor tags
      Array.from(node.childNodes).forEach(processTextNodes);
    }
  };
  
  // Process all text nodes
  Array.from(tempDiv.childNodes).forEach(processTextNodes);
  
  // Get the processed HTML
  let processed = tempDiv.innerHTML;
  
  // Ensure all existing <a> tags have target="_blank" and rel
  processed = processed.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
  
  return processed;
};

function SocialFeed({ posts, setPosts, getPosts, groupId, setSelectedGroup, setShowFeed, selectedGroup, isAdmin, highlightedPostId, setHighlightedPostId }) {
  const token = sessionStorage.getItem("token");
  const [commentingPostId, setCommentingPostId] = useState(null);
  const [commentInput, setCommentInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPostIdForReaction, setCurrentPostIdForReaction] = useState(null);
  const [similarGroups, setSimilarGroups] = useState([])
  const [reposting, setReposting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [expandedComments, setExpandedComments] = useState([]);
  const [openPostModal, setOpenPostModal] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [targetedAds, setTargetedAds] = useState([]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportPostId, setReportPostId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  // Infinite scroll states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);

  const reportReasons = [
    'Spam or misleading content',
    'Harassment or bullying',
    'Inappropriate content',
    'Hate speech',
    'Violence or dangerous behavior',
    'False information',
    'Other'
  ];

  const getFriendlyFileName = (url, mediaType) => {
  const ext = url?.split('.').pop()?.toLowerCase();

  const map = {
    // Documents
    pdf: 'PDF Document',
    docx: 'Word Document',
    doc: 'Word Document',
    pptx: 'PowerPoint Presentation',
    ppt: 'PowerPoint Presentation',
    xlsx: 'Excel Spreadsheet',
    xls: 'Excel Spreadsheet',
    csv: 'CSV File',
    txt: 'Text File',
    rtf: 'Rich Text Document',

    // Images
    // jpg: 'JPEG Image',
    // jpeg: 'JPEG Image',
    // png: 'PNG Image',
    // gif: 'GIF Image',
    // webp: 'WebP Image',
    // svg: 'SVG Image',
    // bmp: 'Bitmap Image',
    // tiff: 'TIFF Image',

    // Audio
    mp3: 'MP3 Audio',
    wav: 'WAV Audio',
    ogg: 'OGG Audio',
    aac: 'AAC Audio',
    m4a: 'M4A Audio',
    flac: 'FLAC Audio',

    // Video
    // mp4: 'MP4 Video',
    // webm: 'WebM Video',
    // mkv: 'MKV Video',
    // avi: 'AVI Video',
    // mov: 'QuickTime Video',
    // flv: 'FLV Video',
    // wmv: 'WMV Video',

    // Archives
    zip: 'ZIP Archive',
    rar: 'RAR Archive',
    '7z': '7-Zip Archive',
    tar: 'TAR Archive',
    gz: 'GZIP Archive',

    // Code / data
    json: 'JSON File',
    xml: 'XML File',
    yaml: 'YAML File',
    yml: 'YAML File',
    sql: 'SQL File',
    log: 'Log File',

    // Executables / installers
    exe: 'Windows Executable',
    msi: 'Windows Installer',
    dmg: 'macOS Installer',
    pkg: 'macOS Package',
    apk: 'Android Package',
    ipa: 'iOS App Package',
  };

  return map[ext]
    ? `${map[ext]}.${ext}`
    : `Download File${ext ? `.${ext}` : ''}`;
};

  const getSimilarGroups = async () => {
    let res = await get("/CommunityGroup/list/popular-community-group/5")
    if (res?.statusCode === 200) {
      setSimilarGroups(res?.data)
    }
    console.log(res)
  }

  const getTargetedAds = async () => {
    try {
      const userId = sessionStorage.getItem('userId');
      const pageName = 'Conversation Feeds';
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/connecthealthadmin/api/ad/find-ad?Id=${userId}&page=${encodeURIComponent(pageName)}`);

      if (response.ok) {
        const data = await response.json();
        const transformedAds = data.map(ad => ({
          id: ad.id,
          imageUrl: ad.bannerImageUrl,
          name: ad.title,
          description: ad.description,
          redirectUrl: ad.redirectUrl,
          displayOrder: 1,
          author: ad.adOwner?.name || '',
          company: ad.adOwner?.company || '',
          time: new Date(ad.createdAt).toLocaleTimeString(),
          startDate: ad.startDate,
          endDate: ad.endDate,
          isTargeted: true
        }));
        setTargetedAds(transformedAds);
      }
    } catch (error) {
      console.error('Error fetching targeted ads:', error);
      setTargetedAds([]);
    }
  };

  useEffect(() => {
    getSimilarGroups();
    getTargetedAds();
  }, [])

  // Fetch more posts for infinite scroll
  const fetchMorePosts = useCallback(async () => {
    if (loadingMore || !hasMore || !groupId) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const res = await get(`/posts/get-group-posts-including-comments?groupId=${groupId}&pageNumber=${nextPage}&pageSize=10`);
      
      if (res?.posts?.length > 0) {
        setPosts(prevPosts => [...prevPosts, ...res.posts]);
        setCurrentPage(nextPage);
        setTotalPages(res.totalPages);
        setHasMore(nextPage < res.totalPages);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching more posts:', error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, groupId, hasMore, loadingMore, setPosts]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          fetchMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [fetchMorePosts, hasMore, loadingMore]);

  // Reset pagination when group changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
  }, [groupId]);

  const handleShowMoreComments = (postId) => {
    setExpandedComments(prev => {
      if (prev.includes(postId)) {
        return prev.filter(id => id !== postId);
      }
      return [...prev, postId];
    });
  };

  const handleCommentClick = (postId) => {
    setCommentingPostId(commentingPostId === postId ? null : postId);
    setCommentInput('');
  };

  const handlePublishComment = async (postId) => {
    setCommenting(true);
    try {
      const payload = {
        postId: postId,
        memberId: 0,
        employeeId: Number(sessionStorage.getItem("userId")),
        commentText: commentInput,
      }

      let res = await post("/Posts/add-post-comments", payload)
      console.log(res)
      if (res?.message === "Comment added successfully and notification sent.") {
        setCommentInput('');
        setCommentingPostId(null);
        getPosts(groupId);
      }

    } catch (error) {
      console.error('Error publishing comment:', error);
    } finally {
      setCommenting(false);
    }
  };

  const handleOpenReactionPopover = (event, postId) => {
    setAnchorEl(event.currentTarget);
    setCurrentPostIdForReaction(postId);
  };

  const handleCloseReactionPopover = () => {
    setAnchorEl(null);
    setCurrentPostIdForReaction(null);
  };

  const handleReactionSelect = async (reactionType) => {
    setReacting(true);
    try {
      const payload = {
        postId: currentPostIdForReaction,
        employeeId: Number(sessionStorage.getItem("userId")),
        reaction: reactionType
      };

      const response = await post("/Posts/ReactToPost", payload);

      if (response?.message === 'Reaction recorded!') {
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === currentPostIdForReaction) {
            const updatedPost = { ...post };

            if (updatedPost.hasUserReacted && updatedPost[`${reactionType}Count`] > 0) {
              updatedPost[`${reactionType}Count`]--;
              updatedPost.likeCount--;
              updatedPost.hasUserReacted = false;
            } else if (!updatedPost.hasUserReacted) {
              updatedPost[`${reactionType}Count`]++;
              updatedPost.likeCount++;
              updatedPost.hasUserReacted = true;
            }

            return updatedPost;
          }
          return post;
        }));
      }
    } catch (error) {
      console.error('Error reacting to post:', error);
    } finally {
      setReacting(false);
      handleCloseReactionPopover();
    }
  };

  const handleRepost = async (postId) => {
    try {
      setReposting(true);
      const payload = {
        postId: postId,
        memberId: 0,
        employeeId: Number(sessionStorage.getItem("userId")),
      };

      const response = await post(`/Posts/${postId}/Repost`, payload);

      if (response === "Post reposted successfully.") {
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              repostCount: post.repostCount + 1
            };
          }
          return post;
        }));

        getPosts(groupId);
      }
    } catch (error) {
      console.error('Error reposting:', error);
    } finally {
      setReposting(false);
    }
  };

  const handleMenuOpen = (event, postId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedPostId(postId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedPostId(null);
  };

  const handleDeletePost = async (postId) => {
    try {
      await del(`/Posts/${postId}`);
      setPosts(posts.filter(post => post.id !== postId));
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleOpenPostModal = () => {
    setOpenPostModal(true);
  };

  const handleClosePostModal = () => {
    setOpenPostModal(false);
  };

  const submitPost = async (postData) => {
    try {
      console.log('New Post Data:', ...postData);

      let res = await Posts(`/posts`, postData);
      if (res) {
        notification({
          type: 'success',
          message: 'Post created successfully!',
          duration: 3000,
        });
        getPosts(groupId);
        setOpenPostModal(false);
      }
    } catch (error) {
      console.error('Error creating post:', error);
      notification({
        type: 'error',
        message: 'Failed to create post',
        duration: 3000,
      });
    }
  };

  const handleRestrictedAction = () => {
    if (!token) {
      setShowAuthModal(true);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'reaction-popover' : undefined;

  const isYouTubeLink = (url) => {
    const regex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return regex.test(url);
  };

  const isMediaDocument = (mediaType) => {
    if (!mediaType) return false;
    return !mediaType.startsWith('image/') && !mediaType.startsWith('video/');
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return 'Download File';
    return url.split('/').pop() || 'Download File';
  };

  const downloadFile = async (fileUrl, fileName) => {
    try {
      const fullUrl = process.env.REACT_APP_MEDIA_URL + fileUrl;

      const response = await fetch(fullUrl, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadBlob = new Blob([blob], { type: 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(downloadBlob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || getFileNameFromUrl(fileUrl);

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      notification({
        type: 'success',
        message: 'Download started!',
        duration: 2000,
      });

    } catch (error) {
      console.error('Error downloading file:', error);
      window.open(process.env.REACT_APP_MEDIA_URL + fileUrl, '_blank');
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return videoIdMatch ? `https://www.youtube.com/embed/${videoIdMatch[1]}` : null;
  };

  // Fixed video player initialization
  useEffect(() => {
    posts.forEach((post) => {
      if (post.mediaUrl && post.mediaType?.startsWith('video/')) {
        const videoElement = document.getElementById(`video-${post.id}`);
        
        if (videoElement && !videoElement.dataset.initialized) {
          videoElement.dataset.initialized = 'true';
          
          // Check if it's an HLS stream (m3u8)
          if (post.mediaType === 'application/vnd.apple.mpegurl' || post.mediaUrl.includes('.m3u8')) {
            if (Hls.isSupported()) {
              const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
              });
              
              const fullUrl = post.mediaUrl.startsWith('http') 
                ? post.mediaUrl 
                : process.env.REACT_APP_MEDIA_URL + post.mediaUrl;
              
              hls.loadSource(fullUrl);
              hls.attachMedia(videoElement);
              
              hls.on(Hls.Events.MANIFEST_PARSED, () => {
                console.log('HLS manifest loaded for post', post.id);
              });
              
              hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS error:', data);
                if (data.fatal) {
                  switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                      hls.startLoad();
                      break;
                    case Hls.ErrorTypes.MEDIA_ERROR:
                      hls.recoverMediaError();
                      break;
                    default:
                      hls.destroy();
                      break;
                  }
                }
              });
            } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
              // Safari native HLS support
              const fullUrl = post.mediaUrl.startsWith('http') 
                ? post.mediaUrl 
                : process.env.REACT_APP_MEDIA_URL + post.mediaUrl;
              videoElement.src = fullUrl;
            }
          }
        }
      }
    });
  }, [posts]);

  useEffect(() => {
    if (highlightedPostId && posts.length > 0) {
      setTimeout(() => {
        const postElement = document.getElementById(`post-${highlightedPostId}`);
        if (postElement) {
          postElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          setTimeout(() => {
            setHighlightedPostId(null);
          }, 3000);
        }
      }, 500);
    }
  }, [highlightedPostId, posts, setHighlightedPostId]);

  const handleFlagPost = async () => {
    if (!reportReason || (reportReason === 'Other' && !customReason.trim())) {
      notification({
        type: 'error',
        message: 'Please select a reason for reporting',
        duration: 3000,
      });
      return;
    }

    setSubmittingReport(true);
    try {
      const payload = {
        id: 0,
        content: reportReason === 'Other' ? customReason : reportReason,
        postId: reportPostId,
        flaggedAt: new Date().toISOString(),
        triggeringKeyword: reportReason === 'Other' ? customReason : reportReason,
        flaggedByUserId: Number(sessionStorage.getItem("userId"))
      };

      const response = await postAdmin(`/posts/${reportPostId}/flag`, payload);

      if (response) {
        notification({
          type: 'success',
          message: 'Post reported successfully. Thank you for helping keep our community safe.',
          duration: 4000,
        });
        handleCloseReportModal();
      }
    } catch (error) {
      console.error('Error flagging post:', error);
      notification({
        type: 'error',
        message: 'Failed to report post. Please try again.',
        duration: 3000,
      });
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleOpenReportModal = (postId) => {
    setReportPostId(postId);
    setReportModalOpen(true);
    handleMenuClose();
  };

  const handleCloseReportModal = () => {
    setReportModalOpen(false);
    setReportPostId(null);
    setReportReason('');
    setCustomReason('');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', py: 3 }}>
        <Container>
          <Grid wrap="nowrap" container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={8}>
              {targetedAds.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <PromoBanner ads={targetedAds} />
                </Box>
              )}

              {posts?.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: '16px',
                    border: '1px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <PostAddIcon
                    sx={{
                      fontSize: 64,
                      color: 'text.secondary',
                      mb: 2
                    }}
                  />
                  <Typography variant="h6" gutterBottom color="text.secondary">
                    No Posts Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {isAdmin
                      ? "Start the conversation by creating the first post"
                      : "There are no posts in this group yet"}
                  </Typography>
                  {isAdmin && token && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PostAddIcon />}
                      onClick={handleOpenPostModal}
                    >
                      Create First Post
                    </Button>
                  )}
                  {!token && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRestrictedAction}
                    >
                      Sign Up or Login
                    </Button>
                  )}
                </Paper>
              ) : (
                <>
                  <Stack spacing={4}>
                    {posts.map((post) => (
                      <Card
                        key={post.id}
                        id={`post-${post.id}`}
                        sx={{
                          mb: 2,
                          ...(highlightedPostId === post.id && {
                            border: '2px solid #28a745',
                            boxShadow: '0 0 15px rgba(40, 167, 69, 0.4)',
                          })
                        }}
                      >
                        {highlightedPostId === post.id && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -1,
                              left: -1,
                              right: -1,
                              height: 4,
                              background: 'linear-gradient(90deg, #28a745, #20c997, #28a745)',
                              borderRadius: '16px 16px 0 0',
                              zIndex: 1,
                            }}
                          />
                        )}

                        <CardContent sx={{ pb: 1, position: 'relative' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar src={post.pictureUrl} alt={post.memberFullName} sx={{ width: 48, height: 48, mr: 1.5 }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6">{post.memberFullName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {/* {format(new Date(post.createdAt), 'EEEE, MMM d, h:mm a')} */}
                              </Typography>
                              {post.groupName && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  In: {post.groupName}
                                </Typography>
                              )}
                            </Box>
                            <IconButton onClick={(event) => handleMenuOpen(event, post.id)}>
                              <MoreVertIcon />
                            </IconButton>
                          </Box>

                          <Box
                            sx={{ 
                              mb: 2,
                              '& a': {
                                color: '#28a745',
                                textDecoration: 'none',
                                fontWeight: 500,
                                wordBreak: 'break-word',
                                '&:hover': {
                                  textDecoration: 'underline',
                                }
                              }
                            }}
                            dangerouslySetInnerHTML={{
                              __html: processPostContent(post.content)
                            }}
                          />

                          {/* Image Media */}
                          {post.mediaUrl && post.mediaType && post.mediaType.startsWith('image/') && (
                            <CardMedia
                              component="img"
                              image={process.env.REACT_APP_MEDIA_URL + post.mediaUrl}
                              alt="Post image"
                              sx={{ borderRadius: '8px', mb: 2 }}
                            />
                          )}

                          {/* Video Media - Fixed */}
                          {post.mediaUrl && post.mediaType && post.mediaType.startsWith('video/') && (
                            <Box sx={{ position: 'relative', mb: 2, borderRadius: '8px', overflow: 'hidden' }}>
                              <video
                                id={`video-${post.id}`}
                                controls
                                preload="metadata"
                                style={{ 
                                  width: '100%', 
                                  height: 'auto', 
                                  display: 'block', 
                                  borderRadius: '8px',
                                  backgroundColor: '#000'
                                }}
                                poster={post.thumbnailUrl || undefined}
                              >
                                {!(post.mediaType === 'application/vnd.apple.mpegurl' || post.mediaUrl.includes('.m3u8')) && (
                                  <source 
                                    src={post.mediaUrl.startsWith('http') ? post.mediaUrl : process.env.REACT_APP_MEDIA_URL + post.mediaUrl} 
                                    type={post.mediaType} 
                                  />
                                )}
                                Your browser does not support the video tag.
                              </video>
                            </Box>
                          )}

                          {/* Document Media */}
                          {post.mediaUrl && post.mediaType && isMediaDocument(post.mediaType) && (
                            <Box sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0',
                              backgroundColor: '#f9f9f9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: '#f0f0f0',
                                borderColor: '#28a745',
                              }
                            }}
                              onClick={() => downloadFile(post.mediaUrl, getFriendlyFileName(post.mediaUrl, post.mediaType))}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                                <FileDownloadIcon sx={{ color: '#28a745', fontSize: '1.8rem' }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                        {getFriendlyFileName(post.mediaUrl, post.mediaType)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Click to download • Downloadable file
                                  </Typography>
                                </Box>
                              </Box>
                              <FileDownloadIcon sx={{ color: '#999', fontSize: '1.5rem' }} />
                            </Box>
                          )}

                          {/* YouTube Embed */}
                          {post.mediaUrl && isYouTubeLink(post.mediaUrl) && (
                            <Box sx={{ position: 'relative', mb: 2, borderRadius: '8px', overflow: 'hidden' }}>
                              <iframe
                                src={getYouTubeEmbedUrl(post.mediaUrl)}
                                title="YouTube video"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{ width: '100%', height: '315px', borderRadius: '8px', border: 'none' }}
                              />
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', py: 1, borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                            <Tooltip title="React to this post" arrow>
                              <Button
                                color="inherit"
                                startIcon={post.hasUserReacted ? <ThumbUpIcon color="primary" /> : <ThumbUpOutlinedIcon />}
                                onClick={token ? (event) => handleOpenReactionPopover(event, post.id) : handleRestrictedAction}
                                aria-describedby={id}
                                disabled={reacting}
                              >
                                {reacting ? 'Reacting...' : getTotalReactions(post)}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Comment on this post" arrow>
                              <Button
                                color="inherit"
                                startIcon={<ChatBubbleOutlineIcon />}
                                onClick={token ? () => handleCommentClick(post.id) : handleRestrictedAction}
                              >
                                {post.comments?.length || 0}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Repost this post" arrow>
                              <Button
                                color="inherit"
                                startIcon={<Repeat />}
                                onClick={token ? () => handleRepost(post?.id) : handleRestrictedAction}
                                disabled={reposting}
                              >
                                {reposting ? 'Reposting...' : post.repostCount}
                              </Button>
                            </Tooltip>
                          </Box>

                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            py: 1,
                            px: 2,
                            gap: 1
                          }}>
                            {reactions.map(reaction => {
                              const count = post[reaction.key];
                              return count > 0 ? (
                                <Box
                                  key={reaction.type}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                    borderRadius: '12px',
                                    padding: '2px 8px',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5
                                    }}
                                  >
                                    {reaction.emoji} {count}
                                  </Typography>
                                </Box>
                              ) : null;
                            })}
                            {!reactions.some(r => post[r.key] > 0) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                              >
                                Be the first to react
                              </Typography>
                            )}
                          </Box>
                        </CardContent>

                        {commentingPostId === post.id && (
                          <CardContent sx={{ pt: 0, px: 2, pb: 1 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, mr: 1, flexShrink: 0 }} />
                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                placeholder="Type a message here"
                                value={commentInput}
                                onChange={(e) => setCommentInput(e.target.value)}
                                sx={{ mr: 1, backgroundColor: '#f5f5f5', borderRadius: '8px', '& .MuiOutlinedInput-root': { paddingRight: '8px' } }}
                                InputProps={{
                                  endAdornment: (
                                    <Stack direction="row" spacing={0.5} sx={{ alignSelf: 'flex-end', pb: 0.5 }}>
                                      <IconButton size="small">
                                        <CameraAltOutlinedIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small">
                                        <VideocamOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  )
                                }}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              onClick={() => handlePublishComment(post.id)}
                              disabled={commenting}
                              sx={{ mt: 1 }}
                            >
                              {commenting ? 'Publishing...' : 'Publish Post'}
                            </Button>
                          </CardContent>
                        )}

                        {post.comments?.length > 0 && (
                          <CardContent sx={{ pt: 0 }}>
                            {(expandedComments.includes(post.id) ? post.comments : post.comments.slice(0, 3)).map((comment) => (
                              <Box key={comment.id} sx={{ display: 'flex', mb: 2 }}>
                                <Avatar
                                  src={comment.profilePictureUrl}
                                  sx={{ width: 32, height: 32, mr: 1 }}
                                />
                                <Box>
                                  <Typography variant="subtitle2">{comment.commenterFullName}</Typography>
                                  <Typography variant="body2" color="text.secondary" component="div">
                                    {linkifyText(comment.commentText)}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                            {post.comments.length > 3 && (
                              <Button
                                size="small"
                                onClick={() => handleShowMoreComments(post.id)}
                                startIcon={expandedComments.includes(post.id) ?
                                  <KeyboardArrowUpIcon /> :
                                  <KeyboardArrowDownIcon />
                                }
                              >
                                {expandedComments.includes(post.id) ? 'Show less' : `Show ${post.comments.length - 3} more comments`}
                              </Button>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    ))}
                  </Stack>

                  {/* Infinite scroll observer target */}
                  <Box ref={observerTarget} sx={{ py: 2, textAlign: 'center' }}>
                    {loadingMore && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Loading more posts...
                        </Typography>
                      </Box>
                    )}
                    {!hasMore && posts.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        You've reached the end of the feed
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Grid>

            <Grid sx={{ width: "50%", display: { xs: 'none', md: 'block' } }} item md={3}>
              <Grid item md={3}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: '16px' }}>
                  <Typography variant="h6" gutterBottom>
                    Similar Group
                  </Typography>
                  <Stack spacing={2}>
                    {similarGroups.map((group) => {
                      const isActive = selectedGroup?.id === group.id;
                      return (
                        <Card
                          key={group.id}
                          elevation={0}
                          onClick={() => { setSelectedGroup(group); setShowFeed(false) }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            borderRadius: '12px',
                            border: isActive ? '2px solid #28a745' : '1px solid #eee',
                            backgroundColor: isActive ? 'rgba(46,125,50,0.1)' : 'transparent',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s, border-color 0.2s',
                            '&:hover': {
                              backgroundColor: isActive
                                ? 'rgba(46,125,50,0.15)'
                                : '#f5f5f5',
                            },
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={group?.pictureUrl || defaultImage}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = defaultImage;
                            }}
                            alt={group.name}
                            sx={{
                              width: 80,
                              height: 60,
                              borderRadius: '8px',
                              mr: 1.5,
                              flexShrink: 0,
                              objectFit: 'cover'
                            }}
                          />
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {group.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: '1.2em',
                                maxHeight: '2.4em'
                              }}
                            >
                              {group.description}
                            </Typography>
                          </Box>
                          <IconButton size="small">
                            <ChevronRightIcon />
                          </IconButton>
                        </Card>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Container>

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseReactionPopover}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: {
              borderRadius: '25px',
              p: 0.5,
              boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
            }
          }}
        >
          <Stack direction="row" spacing={0.5} sx={{ p: 0.5 }}>
            {reactions.map((reaction) => (
              <IconButton
                key={reaction.type}
                aria-label={reaction.type}
                onClick={() => handleReactionSelect(reaction.type)}
                sx={{
                  fontSize: '1.5rem',
                  p: 1,
                  transition: 'transform 0.1s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    backgroundColor: 'rgba(0,0,0,0.04)',
                  }
                }}
              >
                {reaction.emoji}
              </IconButton>
            ))}
          </Stack>
        </Popover>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
            }
          }}
        >
          {isAdmin && (
            <MenuItem
              onClick={() => {
                handleDeletePost(selectedPostId);
                handleMenuClose();
              }}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.lighter',
                }
              }}
            >
              <DeleteIcon sx={{ mr: 1 }} />
              Delete Post
            </MenuItem>
          )}
          {token && (
            <MenuItem
              onClick={() => handleOpenReportModal(selectedPostId)}
              sx={{
                color: 'warning.main',
                '&:hover': {
                  backgroundColor: 'warning.lighter',
                }
              }}
            >
              <FlagIcon sx={{ mr: 1 }} />
              Report Post
            </MenuItem>
          )}
          {!isAdmin && !token && (
            <MenuItem disabled sx={{ color: 'text.disabled' }}>
              <Typography variant="body2">No actions available</Typography>
            </MenuItem>
          )}
        </Menu>

        <PostModal
          id={groupId}
          open={openPostModal}
          onSubmit={submitPost}
          handleClose={handleClosePostModal}
        />

        <Modal
          open={reportModalOpen}
          onClose={handleCloseReportModal}
          aria-labelledby="report-modal-title"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500 },
              maxWidth: 500,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
              p: 0,
              outline: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ReportIcon color="warning" />
                <Typography id="report-modal-title" variant="h6" fontWeight={600}>
                  Report Post
                </Typography>
              </Box>
              <IconButton onClick={handleCloseReportModal} size="small">
                <Close />
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Help us understand what's happening with this post. Your report is anonymous.
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  component="legend"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 2,
                    fontSize: '0.95rem'
                  }}
                >
                  Why are you reporting this post?
                </FormLabel>
                <RadioGroup
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  sx={{ gap: 0.5 }}
                >
                  {reportReasons.map((reason) => (
                    <FormControlLabel
                      key={reason}
                      value={reason}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-checked': {
                              color: 'warning.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                          {reason}
                        </Typography>
                      }
                      sx={{
                        mx: 0,
                        py: 0.5,
                        px: 1,
                        borderRadius: 2,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {reportReason === 'Other' && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Please describe the issue..."
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'grey.50',
                        fontSize: '0.9rem',
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                p: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50',
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCloseReportModal}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleFlagPost}
                disabled={submittingReport || !reportReason || (reportReason === 'Other' && !customReason.trim())}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  minWidth: 100,
                }}
              >
                {submittingReport ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    Reporting...
                  </Box>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </Box>
          </Box>
        </Modal>

        <Modal
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          aria-labelledby="auth-modal-title"
          aria-describedby="auth-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 300,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography id="auth-modal-title" variant="h6" component="h2" gutterBottom>
              Sign Up or Login
            </Typography>
            <Typography id="auth-modal-description" variant="body2" color="text.secondary" gutterBottom>
              Please sign up or log in to access this feature.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <Button variant="contained" color="primary" onClick={() => window.location.href = '/login'}>
                Login
              </Button>
              <Button variant="outlined" color="primary" onClick={() => window.location.href = '/signUp'}>
                Sign Up
              </Button>
            </Stack>
          </Box>
        </Modal>
      </Box>
    </ThemeProvider>

  );
}

export default SocialFeed;